// Service Manager - Manages all external service integrations
import localAuthService from './localAuthService';
import localPaymentService from './localPaymentService';
import localShippingService from './localShippingService';
import localNotificationService from './localNotificationService';
import realFedExService from './realFedExService';
import realDHLService from './realDHLService';
import realStripeService from './realStripeService';
import realShopifyService from './realShopifyService';
import realSendGridService from './realSendGridService';

class ServiceManager {
  constructor() {
    this.mode = this.getMode();
    this.services = this.initializeServices();
  }

  // Get current mode from localStorage or default to 'local'
  getMode() {
    const savedMode = localStorage.getItem('silva_mode');
    return savedMode || 'local';
  }

  // Set mode and update localStorage
  setMode(mode) {
    this.mode = mode;
    localStorage.setItem('silva_mode', mode);
    this.services = this.initializeServices();
  }

  // Initialize services based on current mode
  initializeServices() {
    if (this.mode === 'real') {
      return {
        auth: localAuthService, // Keep auth local for now
        payment: realStripeService,
        shipping: {
          fedex: realFedExService,
          dhl: realDHLService
        },
        ecommerce: realShopifyService,
        notification: realSendGridService
      };
    } else {
      return {
        auth: localAuthService,
        payment: localPaymentService,
        shipping: localShippingService,
        ecommerce: localShippingService, // Use shipping service for ecommerce in local mode
        notification: localNotificationService
      };
    }
  }

  // Get current mode
  getCurrentMode() {
    return this.mode;
  }

  // Check if real mode is available (has API keys)
  isRealModeAvailable() {
    const requiredKeys = [
      'REACT_APP_FEDEX_API_KEY',
      'REACT_APP_DHL_API_KEY',
      'REACT_APP_STRIPE_PUBLISHABLE_KEY',
      'REACT_APP_SHOPIFY_ACCESS_TOKEN',
      'REACT_APP_SENDGRID_API_KEY'
    ];

    return requiredKeys.every(key => process.env[key]);
  }

  // Get available modes
  getAvailableModes() {
    const modes = ['local'];
    if (this.isRealModeAvailable()) {
      modes.push('real');
    }
    return modes;
  }

  // Auth Service Methods
  async login(email, password) {
    return await this.services.auth.login(email, password);
  }

  async register(userData) {
    return await this.services.auth.register(userData);
  }

  async logout() {
    return await this.services.auth.logout();
  }

  async forgotPassword(email) {
    return await this.services.auth.forgotPassword(email);
  }

  async resetPassword(token, newPassword) {
    return await this.services.auth.resetPassword(token, newPassword);
  }

  // Payment Service Methods
  async processPayment(paymentData) {
    if (this.mode === 'real') {
      return await this.services.payment.processPayment(
        paymentData.paymentMethodId,
        paymentData.amount,
        paymentData.currency,
        paymentData.metadata
      );
    } else {
      return await this.services.payment.processPayment(paymentData);
    }
  }

  async createCustomer(customerData) {
    if (this.mode === 'real') {
      return await this.services.payment.createCustomer(
        customerData.email,
        customerData.name,
        customerData.phone
      );
    } else {
      return await this.services.payment.createCustomer(customerData);
    }
  }

  async getPaymentMethods(customerId) {
    if (this.mode === 'real') {
      return await this.services.payment.getCustomerPaymentMethods(customerId);
    } else {
      return await this.services.payment.getPaymentMethods(customerId);
    }
  }

  async getPaymentHistory(customerId) {
    if (this.mode === 'real') {
      return await this.services.payment.getPaymentHistory(customerId);
    } else {
      return await this.services.payment.getPaymentHistory(customerId);
    }
  }

  // Shipping Service Methods
  async getShippingRates(origin, destination, weight, dimensions) {
    if (this.mode === 'real') {
      // Get rates from both FedEx and DHL
      const [fedexRates, dhlRates] = await Promise.allSettled([
        this.services.shipping.fedex.getRates(origin, destination, weight, dimensions),
        this.services.shipping.dhl.getRates(origin, destination, weight, dimensions)
      ]);

      const rates = [];
      if (fedexRates.status === 'fulfilled') {
        rates.push(...fedexRates.value);
      }
      if (dhlRates.status === 'fulfilled') {
        rates.push(...dhlRates.value);
      }

      return rates;
    } else {
      return await this.services.shipping.getRates(origin, destination, weight, dimensions);
    }
  }

  async createShipment(shipmentData) {
    if (this.mode === 'real') {
      // Choose carrier based on service code or default to FedEx
      const carrier = shipmentData.serviceCode?.includes('DHL') ? 'dhl' : 'fedex';
      return await this.services.shipping[carrier].createShipment(shipmentData);
    } else {
      return await this.services.shipping.createShipment(shipmentData);
    }
  }

  async trackShipment(trackingNumber, carrier = 'fedex') {
    if (this.mode === 'real') {
      const service = carrier.toLowerCase() === 'dhl' ? 'dhl' : 'fedex';
      return await this.services.shipping[service].trackShipment(trackingNumber);
    } else {
      return await this.services.shipping.trackShipment(trackingNumber);
    }
  }

  // E-commerce Service Methods
  async getProducts(limit = 50, page = 1) {
    if (this.mode === 'real') {
      return await this.services.ecommerce.getProducts(limit, page);
    } else {
      return await this.services.ecommerce.getProducts(limit, page);
    }
  }

  async getProduct(productId) {
    if (this.mode === 'real') {
      return await this.services.ecommerce.getProduct(productId);
    } else {
      return await this.services.ecommerce.getProduct(productId);
    }
  }

  async createOrder(orderData) {
    if (this.mode === 'real') {
      return await this.services.ecommerce.createOrder(orderData);
    } else {
      return await this.services.ecommerce.createOrder(orderData);
    }
  }

  async getOrders(limit = 50, status = null) {
    if (this.mode === 'real') {
      return await this.services.ecommerce.getOrders(limit, status);
    } else {
      return await this.services.ecommerce.getOrders(limit, status);
    }
  }

  async updateOrderStatus(orderId, status) {
    if (this.mode === 'real') {
      return await this.services.ecommerce.updateOrderStatus(orderId, status);
    } else {
      return await this.services.ecommerce.updateOrderStatus(orderId, status);
    }
  }

  // Notification Service Methods
  async sendEmail(to, subject, content, templateId = null) {
    if (this.mode === 'real') {
      return await this.services.notification.sendEmail(to, subject, content, templateId);
    } else {
      return await this.services.notification.sendEmail(to, subject, content);
    }
  }

  async sendShipmentNotification(userEmail, shipmentData) {
    if (this.mode === 'real') {
      return await this.services.notification.sendShipmentNotification(userEmail, shipmentData);
    } else {
      return await this.services.notification.sendShipmentNotification(userEmail, shipmentData);
    }
  }

  async sendTrackingUpdate(userEmail, trackingData) {
    if (this.mode === 'real') {
      return await this.services.notification.sendTrackingUpdate(userEmail, trackingData);
    } else {
      return await this.services.notification.sendTrackingUpdate(userEmail, trackingData);
    }
  }

  async sendPaymentConfirmation(userEmail, paymentData) {
    if (this.mode === 'real') {
      return await this.services.notification.sendPaymentConfirmation(userEmail, paymentData);
    } else {
      return await this.services.notification.sendPaymentConfirmation(userEmail, paymentData);
    }
  }

  async sendOrderConfirmation(userEmail, orderData) {
    if (this.mode === 'real') {
      return await this.services.notification.sendOrderConfirmation(userEmail, orderData);
    } else {
      return await this.services.notification.sendOrderConfirmation(userEmail, orderData);
    }
  }

  async sendPasswordReset(userEmail, resetToken) {
    if (this.mode === 'real') {
      return await this.services.notification.sendPasswordReset(userEmail, resetToken);
    } else {
      return await this.services.notification.sendPasswordReset(userEmail, resetToken);
    }
  }

  async sendWelcomeEmail(userEmail, userName) {
    if (this.mode === 'real') {
      return await this.services.notification.sendWelcomeEmail(userEmail, userName);
    } else {
      return await this.services.notification.sendWelcomeEmail(userEmail, userName);
    }
  }

  // Utility Methods
  getServiceStatus() {
    const status = {
      mode: this.mode,
      availableModes: this.getAvailableModes(),
      realModeAvailable: this.isRealModeAvailable(),
      services: {
        auth: 'local', // Always local for now
        payment: this.mode,
        shipping: this.mode,
        ecommerce: this.mode,
        notification: this.mode
      }
    };

    if (this.mode === 'real') {
      status.apiKeys = {
        fedex: !!process.env.REACT_APP_FEDEX_API_KEY,
        dhl: !!process.env.REACT_APP_DHL_API_KEY,
        stripe: !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
        shopify: !!process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN,
        sendgrid: !!process.env.REACT_APP_SENDGRID_API_KEY
      };
    }

    return status;
  }

  // Test service connectivity
  async testServiceConnectivity() {
    const results = {
      mode: this.mode,
      tests: {}
    };

    try {
      // Test auth service
      results.tests.auth = { status: 'ok', message: 'Auth service available' };
    } catch (error) {
      results.tests.auth = { status: 'error', message: error.message };
    }

    try {
      // Test payment service
      if (this.mode === 'real') {
        // Test Stripe connectivity
        await this.services.payment.getElements();
        results.tests.payment = { status: 'ok', message: 'Stripe service available' };
      } else {
        results.tests.payment = { status: 'ok', message: 'Local payment service available' };
      }
    } catch (error) {
      results.tests.payment = { status: 'error', message: error.message };
    }

    try {
      // Test shipping service
      if (this.mode === 'real') {
        // Test FedEx connectivity
        await this.services.shipping.fedex.getAccessToken();
        results.tests.shipping = { status: 'ok', message: 'FedEx/DHL services available' };
      } else {
        results.tests.shipping = { status: 'ok', message: 'Local shipping service available' };
      }
    } catch (error) {
      results.tests.shipping = { status: 'error', message: error.message };
    }

    try {
      // Test ecommerce service
      if (this.mode === 'real') {
        // Test Shopify connectivity
        await this.services.ecommerce.getProducts(1);
        results.tests.ecommerce = { status: 'ok', message: 'Shopify service available' };
      } else {
        results.tests.ecommerce = { status: 'ok', message: 'Local ecommerce service available' };
      }
    } catch (error) {
      results.tests.ecommerce = { status: 'error', message: error.message };
    }

    try {
      // Test notification service
      if (this.mode === 'real') {
        // Test SendGrid connectivity
        results.tests.notification = { status: 'ok', message: 'SendGrid service available' };
      } else {
        results.tests.notification = { status: 'ok', message: 'Local notification service available' };
      }
    } catch (error) {
      results.tests.notification = { status: 'error', message: error.message };
    }

    return results;
  }
}

// Create singleton instance
const serviceManager = new ServiceManager();

export default serviceManager; 